import * as React from 'react';
import { createRoot } from 'react-dom/client';
import { Toaster } from 'react-hot-toast';

import { TanstackQueryProvider } from '@/providers';
import * as Sentry from '@sentry/react';

import App from './App';
import { SENTRY_DSN, VITE_ENVIRONMENT } from './constants/common';
import { useAccountStore } from './store/account';

import './index.css';

// Ensure React is properly initialized
if (!window.React) {
  window.React = React;
}

const isProdOrStaging = ['production', 'staging'].includes(VITE_ENVIRONMENT);
const accountDetails = useAccountStore.getState().userDetails;

Sentry.init({
  dsn: SENTRY_DSN,
  environment: VITE_ENVIRONMENT,
  integrations: [
    ...(isProdOrStaging
      ? [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration({
            maskAllText: false,
          }),
        ]
      : []),
    Sentry.httpContextIntegration(),
  ],
  tracesSampleRate: isProdOrStaging ? 0.25 : 0,
  replaysSessionSampleRate: isProdOrStaging ? 0.9 : 0,
  replaysOnErrorSampleRate: isProdOrStaging ? 1.0 : 0,
});
Sentry.setUser({
  id: accountDetails?.id,
});

const root = createRoot(document.getElementById('root')!, {
  // Callback called when an error is thrown and not caught by an ErrorBoundary.
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    console.warn('Uncaught error', error, errorInfo.componentStack);
  }),
  // Callback called when React catches an error in an ErrorBoundary.
  onCaughtError: Sentry.reactErrorHandler(),
  // Callback called when React automatically recovers from errors.
  onRecoverableError: Sentry.reactErrorHandler(),
});

root.render(
  <React.StrictMode>
    <TanstackQueryProvider>
      <App />
      <Toaster />
    </TanstackQueryProvider>
  </React.StrictMode>
);
